import React, { useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import StepLayout from '../components/StepLayout';
import { api } from '../api';

// seller-agent-messenger-step (2026-09-28): between PaymentDetails and
// Confirmation, give the seller their dedicated agent on Facebook Messenger so
// we have a channel they answer on after payout.
//
// - The agent comes from POST /sessions/{id}/messenger-agent, which assigns one
//   (least-loaded among the logged-in FB profiles for the seller's country,
//   sticky per seller) and returns { agent: {name, photoUrl, link} | null }.
// - No agent (no profile for the country, the step switched off server-side, or
//   any error): the step skips itself once and renders nothing. A seller is
//   never held here.
// - Payout is NOT held (Filip, D2 soft): "I've messaged them" records the
//   seller's own confirm and moves on; if saving the confirm fails the seller
//   still moves on.
// - Desktop gets a QR code of the same m.me link, so the seller can open it on
//   the phone where Messenger lives.

const DESKTOP_QUERY = '(min-width: 768px)';

function useIsDesktop() {
  const get = () => {
    try {
      return typeof window !== 'undefined' && !!window.matchMedia
        && window.matchMedia(DESKTOP_QUERY).matches;
    } catch {
      return false;
    }
  };
  const [isDesktop, setIsDesktop] = useState(get);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(DESKTOP_QUERY);
    const onChange = () => setIsDesktop(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, []);
  return isDesktop;
}

function initials(name) {
  return (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
}

export default function StepMessageYourAgent({ sessionId, onNext }) {
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opened, setOpened] = useState(false);
  const [saving, setSaving] = useState(false);
  const [photoFailed, setPhotoFailed] = useState(false);
  const skippedRef = useRef(false);
  const isDesktop = useIsDesktop();

  useEffect(() => {
    let cancelled = false;
    const skip = () => {
      if (cancelled || skippedRef.current) return;
      skippedRef.current = true;
      onNext();
    };
    (async () => {
      try {
        const res = await api.getMessengerAgent(sessionId);
        if (cancelled) return;
        if (!res || !res.agent || !res.agent.link) {
          skip();
          return;
        }
        setAgent(res.agent);
        if (res.confirmedAt) setOpened(true);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[takeover-app] messenger agent unavailable; skipping:', err?.message || err);
        skip();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // onNext is re-created by App on every render; run once per session.
  }, [sessionId]);

  const confirm = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await api.confirmMessengerAgent(sessionId);
    } catch (err) {
      // The confirm is a record, not a gate: never hold the seller on it.
      // eslint-disable-next-line no-console
      console.warn('[takeover-app] messenger confirm not saved:', err?.message || err);
    }
    onNext();
  };

  if (loading || !agent) {
    return (
      <StepLayout title="One last thing" hideTrustpilot>
        <p className="agent-step__loading" aria-live="polite">Loading...</p>
      </StepLayout>
    );
  }

  const firstName = (agent.name || '').split(/\s+/)[0] || 'your agent';

  return (
    <StepLayout title="Message your agent" hideTrustpilot>
      <p className="agent-step__intro">
        {firstName} is your dedicated agent. Send them a quick hello on Messenger now,
        so you have each other&rsquo;s contact. Stay responsive when we message you,
        that&rsquo;s all it takes to keep earning.
      </p>

      <section className="agent-card" aria-label="Your agent">
        {agent.photoUrl && !photoFailed ? (
          <img
            className="agent-card__photo"
            src={agent.photoUrl}
            alt={agent.name}
            width="72"
            height="72"
            onError={() => setPhotoFailed(true)}
          />
        ) : (
          <div className="agent-card__photo agent-card__photo--initials" aria-hidden="true">
            {initials(agent.name)}
          </div>
        )}
        <div className="agent-card__text">
          <div className="agent-card__name">{agent.name}</div>
          <div className="agent-card__role">Your dedicated agent</div>
        </div>
      </section>

      <a
        className="primary-cta agent-step__open"
        href={agent.link}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => setOpened(true)}
      >
        Open Messenger
      </a>

      {isDesktop && (
        <div className="agent-step__qr" data-testid="agent-qr">
          <QRCodeSVG value={agent.link} size={148} level="M" />
          <p className="agent-step__qr-note">
            On your computer? Scan this with your phone to open Messenger there.
          </p>
        </div>
      )}

      <button
        type="button"
        className={opened ? 'primary-cta agent-step__confirm' : 'agent-step__confirm agent-step__confirm--secondary'}
        onClick={confirm}
        disabled={saving}
        aria-disabled={saving}
      >
        {saving ? 'Saving...' : `I've messaged ${firstName}`}
      </button>
    </StepLayout>
  );
}
